import { graphql } from 'app/generated/gql'
import { observer } from 'mobx-react-lite'
import { withZod } from '@remix-validated-form/with-zod'
import { compact } from 'lodash'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { useConfirmation } from '../ConfirmationContext'
import { FollowUpFormV2 } from './FollowUpFormV2'
import type { FollowUpFormProps } from './FollowUpFormV2'
import { QuickCreateTaskModal } from 'app/components/tasks/QuickCreateTaskModal'
import useFakeProgress from 'app/hooks/fakeProgress'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import type { ActivityJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import type { FollowUpJson, FollowUpType } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { gqlMutation } from 'app/lib/graphqlClient'
import { NOTE_MAX_LENGTH, NOTE_TOO_LONG_MESSAGE } from 'app/lib/noteMaxLength'

import { logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'

const FOLLOWUP_FIELDS = `id orgId workspaceId locationId contactId ownerName ownerId appointmentDate followUpDate isConnected isScheduled description createdDate updatedDate`

function mapFollowUpResponse(data: Record<string, unknown>): FollowUpJson {
  return {
    ...data,
    owner: data.ownerId ? { id: data.ownerId as string, name: data.ownerName as string } : undefined,
  } as FollowUpJson
}

const CREATE_FOLLOWUP_MUTATION = graphql(`
  mutation CreateFollowUp($input: CreateFollowUpInput!) {
    createFollowUp(input: $input) {
      id
      orgId
      workspaceId
      locationId
      contactId
      ownerName
      ownerId
      appointmentDate
      followUpDate
      isConnected
      isScheduled
      description
      createdDate
      updatedDate
    }
  }
`)

const UPDATE_FOLLOWUP_MUTATION = graphql(`
  mutation UpdateFollowUp($id: ID!, $input: UpdateFollowUpInput!) {
    updateFollowUp(id: $id, input: $input) {
      id
      orgId
      workspaceId
      locationId
      contactId
      ownerName
      ownerId
      appointmentDate
      followUpDate
      isConnected
      isScheduled
      description
      createdDate
      updatedDate
    }
  }
`)

const DELETE_FOLLOWUP_MUTATION = graphql(`
  mutation FollowUpFormDeleteFollowUp($id: ID!) {
    deleteFollowUp(id: $id)
  }
`)

export const followUpValidator = withZod(
  z.object({
    _id: z.string().optional(),
    orgId: z.string().min(1, { message: `Organization Id required` }),
    workspaceId: z.string().min(1, { message: `Workspace Id required` }),
    locationId: z.string().min(1, { message: `Location Id required` }),
    type: z.string().min(1, { message: `Activity type required` }),
    contactId: z.string().min(1, { message: `Contact Id required` }),
    isConnected: z.string().optional(),
    isScheduled: z.string().optional(),
    description: z.string().max(NOTE_MAX_LENGTH, { message: NOTE_TOO_LONG_MESSAGE }).or(z.string().optional()),
    followUpDate: z.string().min(1, { message: `Follow up date required` }),
    appointmentDate: z.string().optional(),
    owner: z
      .object({ id: z.string(), name: z.string(), value: z.string() })
      .transform((owner) => ({ id: owner.id, name: owner.name })),
  }),
)

// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
export const FollowUpForm = observer(function FollowUpForm({
  contact,
  followUp,
  leadStatus,
  changeModalProps,
  onDelete,
  onSubmit,
  onComplete,
}: {
  contact: ContactJson
  followUp?: FollowUpJson
  leadStatus?: ContactJson[`leadStatus`]
  changeModalProps?: () => void
  onDelete?: () => void
  onSubmit: (activity?: ActivityJson, followUp?: FollowUpJson) => void | Promise<void>
  onComplete: () => void
}): ReactElement {
  const user = useUser()
  const [confirm] = useConfirmation()
  const checkPermission = useCheckPermission()
  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const [progressOpen, setProgressOpen] = useState<boolean>(false)
  const [progress, progressStart, progressEnd] = useFakeProgress()
  const [saving, setSaving] = useState(false)
  const isMountedRef = useRef(true)
  const [staff, setStaff] = useState<OptionType | undefined>(() => {
    if (followUp?.owner) return { id: followUp?.owner.id!, name: followUp?.owner.name!, value: followUp?.owner.id! }
    if (user) return { id: user.id!, name: user.name!, value: user.name! }
    return undefined
  })
  const [defaultValues, setDefaultValues] = useState<Record<string, string | undefined>>(() =>
    followUp
      ? {
          appointmentDate: followUp?.appointmentDate,
          followUpDate: followUp?.followUpDate,
          isConnected: followUp?.isConnected === true ? `true` : `false`,
          isScheduled: followUp?.isScheduled === true ? `true` : `false`,
          description: followUp?.description,
        }
      : { isConnected: `false`, isScheduled: `false`, appointmentDate: undefined },
  )
  const [scheduledDisabled, setScheduledDisabled] = useState<boolean>(followUp ? !followUp.isConnected : true)
  const [appointmentDateDisabled, setAppointmentDateDisabled] = useState<boolean>(() => {
    if (leadStatus === `Scheduled`) return false
    if (defaultValues?.isScheduled === `true`) return false
    return true
  })
  const [isConnected, setIsConnected] = useState<boolean>(defaultValues.isConnected === `true`)
  const [isScheduled, setIsScheduled] = useState<boolean>(defaultValues.isScheduled === `true`)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const submitFollowUp = async (data?: Record<string, unknown>) => {
    if (
      data &&
      followUp &&
      (followUp?.isConnected?.toString() !== data.isConnected || followUp?.isScheduled?.toString() !== data.isScheduled)
    ) {
      setProgressOpen(true)
      progressStart()
    }
    setSaving(true)
    let responseFollowUp: FollowUpJson | undefined
    let error: string | undefined
    try {
      if (followUp?.id) {
        // Update existing follow-up
        const owner = data?.owner as { id: string; name: string } | undefined
        const input = {
          isConnected: data?.isConnected === `true`,
          isScheduled: data?.isScheduled === `true`,
          description: data?.description as string | undefined,
          appointmentDate: data?.appointmentDate as string | undefined,
          followUpDate: data?.followUpDate as string | undefined,
          ownerId: owner?.id,
          ownerName: owner?.name,
        }
        const result = await gqlMutation(UPDATE_FOLLOWUP_MUTATION, {
          id: followUp.id,
          input,
        })
        if (result.error) error = result.error.message
        else if (result.data?.updateFollowUp) responseFollowUp = mapFollowUpResponse(result.data.updateFollowUp)
      } else {
        // Create new follow-up
        const owner = data?.owner as { id: string; name: string } | undefined
        const input = {
          contactId: data?.contactId as string,
          locationId: data?.locationId as string,
          isConnected: data?.isConnected === `true`,
          isScheduled: data?.isScheduled === `true`,
          description: data?.description as string | undefined,
          appointmentDate: data?.appointmentDate as string | undefined,
          followUpDate: data?.followUpDate as string | undefined,
          ownerId: owner?.id,
          ownerName: owner?.name,
          noContactUpdate: !!leadStatus,
        }
        const result = await gqlMutation(CREATE_FOLLOWUP_MUTATION, {
          input,
        })
        if (result.error) error = result.error.message
        else if (result.data?.createFollowUp) responseFollowUp = mapFollowUpResponse(result.data.createFollowUp)
      }
    } finally {
      setSaving(false)
    }
    if (error) {
      logError(`Error ${followUp ? `updating` : `creating`} follow up`, error)
      notifyError(`Error ${followUp ? `updating` : `creating`} follow up`)
    } else {
      const isCreate = !followUp

      if (!leadStatus) notifySuccess(`Follow up ${followUp ? `edited` : `created`} successfully`)

      try {
        await onSubmit(undefined, responseFollowUp)
      } catch (e) {
        logError(`Error in onSubmit callback after follow up save`, e)
      }

      // Always close the modal after successful save
      onComplete()

      if (!isMountedRef.current) {
        return
      }

      const hasTaskPermission =
        user?.type === `super` || !staffV2Enabled || checkPermission([`org:pages:tasks`, `pages:tasks`])
      const shouldPromptForTask = isCreate && responseFollowUp?.isScheduled === false && hasTaskPermission

      if (shouldPromptForTask) {
        await confirm({
          title: `Add a Task?`,
          description: `Would you like to add a task? `,
          variant: `info`,
          submitLabel: `Yes`,
          cancelLabel: `No`,
        }).then(() => {
          showModal(
            <QuickCreateTaskModal
              defaultContact={contact}
              workspaceId={contact.workspaceId}
              onTaskCreatedOrUpdated={() => {}}
            />,
            { hideTitle: true, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
            false,
          )
        })
      }
    }
  }

  const deleteHandler = async () => {
    if (!followUp?.id) return
    changeModalProps?.()
    setSaving(true)
    let response
    try {
      response = await gqlMutation(DELETE_FOLLOWUP_MUTATION, { id: followUp?.id }).then((r) => {
        if (r.error) return { error: r.error.message }
        return {}
      })
    } finally {
      setSaving(false)
    }
    progressEnd()
    if (response?.error) {
      logError(`Error deleting follow up`, response.error)
    } else {
      notifySuccess(`Follow up deleted successfully`)
      onDelete?.()
    }
  }

  if (leadStatus && ![`In Progress`, `Scheduled`].includes(leadStatus)) {
    notifyError(`Cannot create follow up for a lead with status ${leadStatus}`)
    onComplete()
  }

  const hiddenInputs = compact([
    { name: `type`, value: `FollowUp` },
    { name: `contactId`, value: contact?.id },
    { name: `orgId`, value: followUp?.orgId ?? contact?.orgId },
    { name: `workspaceId`, value: followUp?.workspaceId ?? contact?.workspaceId },
    { name: `locationId`, value: followUp?.locationId ?? contact?.locationId },
    ...(leadStatus === `In Progress`
      ? [
          { name: `isScheduled`, value: `false` },
          { name: `followUpDate`, value: new Date().toISOString() },
        ]
      : []),
    ...(leadStatus === `Scheduled`
      ? [
          { name: `isConnected`, value: `true` },
          { name: `isScheduled`, value: `true` },
          { name: `followUpDate`, value: new Date().toISOString() },
        ]
      : []),
  ])

  const handleIsConnectedChange = (value: string | boolean) => {
    const connected = typeof value === `boolean` ? value : value === `true`
    setIsConnected(connected)
    if (!connected) {
      setScheduledDisabled(true)
      setAppointmentDateDisabled(true)
      setDefaultValues({ ...defaultValues, isScheduled: `false`, appointmentDate: undefined })
      setIsScheduled(false)
    } else {
      setScheduledDisabled(false)
      setDefaultValues({
        ...defaultValues,
        appointmentDate: new Date().toISOString(),
      })
    }
  }

  const handleIsScheduledChange = (value: string | boolean) => {
    const scheduled = typeof value === `boolean` ? value : value === `true`
    if (!scheduledDisabled) {
      setIsScheduled(scheduled)
      if (!scheduled) {
        setAppointmentDateDisabled(true)
        setDefaultValues({ ...defaultValues, isScheduled: `false`, appointmentDate: undefined })
      } else {
        setAppointmentDateDisabled(false)
        setDefaultValues({ ...defaultValues, isScheduled: `true`, appointmentDate: new Date().toISOString() })
      }
    }
    return !scheduledDisabled
  }

  const props: FollowUpFormProps = {
    contact,
    followUp,
    leadStatus,
    progressOpen,
    progress,
    saving,
    staff,
    defaultValues,
    appointmentDateDisabled,
    hiddenInputs,
    isConnected,
    isScheduled,
    validator: followUpValidator,
    setStaff,
    submitFollowUp,
    deleteHandler,
    handleIsConnectedChange,
    handleIsScheduledChange,
    onComplete,
    progressStart,
    setProgressOpen,
  }

  return <FollowUpFormV2 {...props} />
})
